import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  IconButton,
  CircularProgress,
  Container,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useCookies } from "react-cookie";
import Layout from "../components/layout/GSTlayout";
import config from "../config.js";

const PendingCompliancePage = () => {
 
  const [pendingItems, setPendingItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [cookies] = useCookies(["userId"]);
  const { API_BASE_URL } = config;

  
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      category: "",
      title: "",
      dueDate: new Date().toISOString().split("T")[0],
      description: "",
      status: "upcoming",
      priority: "medium",
    },
  });


  useEffect(() => {
    if (cookies.userId) {
      fetchPendingItems();
    } else {
      setError("User authentication required");
      setLoading(false);
    }
  }, [cookies.userId, refreshKey]);

  const fetchPendingItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getPendingItems?userId=${cookies.userId}`
      );
    

      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data.data)
        ? response.data.data
        : [];

      setPendingItems(items);
      setError(null);
    } catch (error) {
      console.error("Error fetching items:", error);
      setError(
        error.response?.data?.message || "Failed to fetch pending items"
      );
      setPendingItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE_URL}/api/v1/createPendingItem`, {
        ...data,
        userId: cookies.userId,
      });
      setOpenDialog(false);
      reset();
      setRefreshKey((prev) => prev + 1);
      showSnackbar("Item added successfully", "success");
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to add item",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data) => {
    try {
      setLoading(true);
      await axios.put(
        `${API_BASE_URL}/api/v1/updatePendingItems/${selectedItem._id}`,
        {
          ...data,
          userId: cookies.userId,
        }
      );
      setOpenUpdateDialog(false);
      setRefreshKey((prev) => prev + 1);
      showSnackbar("Item updated successfully", "success");
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to update item",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        setLoading(true);
        await axios.delete(
          `${API_BASE_URL}/api/v1/deletePendingItems/${itemId}`
        );
        setRefreshKey((prev) => prev + 1);
        showSnackbar("Item deleted successfully", "success");
      } catch (error) {
        showSnackbar(
          error.response?.data?.message || "Failed to delete item",
          "error"
        );
      } finally {
        setLoading(false);
      }
    }
  };


  const openUpdateMode = (item) => {
    setSelectedItem(item);
    Object.keys(item).forEach((key) => {
      if (
        key !== "_id" &&
        key !== "userId" &&
        key !== "createdAt" &&
        key !== "updatedAt"
      ) {
        setValue(key, item[key]);
      }
    });
    setOpenUpdateDialog(true);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChip = (status, priority) => {
    const configs = {
      overdue: { icon: <WarningAmberIcon />, label: "Overdue", color: "error" },
      upcoming: {
        icon:
          priority === "high" ? (
            <ErrorOutlineIcon />
          ) : (
            <CheckCircleOutlineIcon />
          ),
        label: "Upcoming",
        color: priority === "high" ? "warning" : "success",
      },
    };
    const config = configs[status] || configs.upcoming;
    return (
      <Chip icon={config.icon} label={config.label} color={config.color} />
    );
  };

  const getPriorityChip = (priority) => {
    const colors = {
      high: "error",
      medium: "warning",
      low: "success",
    };
    return (
      <Chip
        label={priority.charAt(0).toUpperCase() + priority.slice(1)}
        color={colors[priority] || "default"}
      />
    );
  };


  const categorizedItems = {
    overdue: pendingItems.filter((item) => item?.status === "overdue") || [],
    upcoming: pendingItems.filter((item) => item?.status === "upcoming") || [],
  };


  const renderTable = (items, title) => (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title} ({items.length})
      </Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item._id} hover>
                <TableCell>{item.category || "N/A"}</TableCell>
                <TableCell>{item.title || "N/A"}</TableCell>
                <TableCell>
                  {item.dueDate
                    ? new Date(item.dueDate).toLocaleDateString()
                    : "N/A"}
                </TableCell>
                <TableCell>{item.description || "N/A"}</TableCell>
                <TableCell>
                  {getStatusChip(item.status, item.priority)}
                </TableCell>
                <TableCell>{getPriorityChip(item.priority)}</TableCell>
                <TableCell>
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => openUpdateMode(item)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(item._id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No items found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderForm = (onSubmit, title, submitLabel) => (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Category is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Category"
                  fullWidth
                  required
                  error={!!errors.category}
                  helperText={errors.category?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="title"
              control={control}
              rules={{ required: "Title is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  fullWidth
                  required
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="dueDate"
              control={control}
              rules={{ required: "Due date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Due Date"
                  type="date"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="priority"
              control={control}
              rules={{ required: "Priority is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  fullWidth
                  label="Priority"
                  error={!!errors.priority}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={4}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Status is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  fullWidth
                  label="Status"
                  error={!!errors.status}
                >
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() =>
            openUpdateDialog ? setOpenUpdateDialog(false) : setOpenDialog(false)
          }
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </form>
  );

  return (
    <Layout>
      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ py: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item>
                    <Typography variant="h4" component="h1">
                      GST Pending Compliance Tracker
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setRefreshKey((prev) => prev + 1)}
                        startIcon={<RefreshIcon />}
                        disabled={loading}
                      >
                        Refresh
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => setOpenDialog(true)}
                        startIcon={<AddIcon />}
                        disabled={loading}
                      >
                        Add Item
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {loading && !openDialog && !openUpdateDialog ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {renderTable(
                      categorizedItems.overdue,
                      "Overdue Compliance Items"
                    )}
                    {renderTable(
                      categorizedItems.upcoming,
                      "Upcoming Compliance Items"
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Dialog
              open={openDialog}
              onClose={() => setOpenDialog(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Add New Compliance Item</DialogTitle>
              {renderForm(onSubmit, "Add New Compliance Item", "Add Item")}
            </Dialog>

            <Dialog
              open={openUpdateDialog}
              onClose={() => setOpenUpdateDialog(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Update Compliance Item</DialogTitle>
              {renderForm(
                handleUpdate,
                "Update Compliance Item",
                "Update Item"
              )}
            </Dialog>

            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                variant="filled"
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>

            <Dialog
              open={Boolean(selectedItem?.deleteConfirmation)}
              onClose={() =>
                setSelectedItem((prev) => ({
                  ...prev,
                  deleteConfirmation: false,
                }))
              }
            >
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete this compliance item? This
                  action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() =>
                    setSelectedItem((prev) => ({
                      ...prev,
                      deleteConfirmation: false,
                    }))
                  }
                >
                  Cancel
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => {
                    if (selectedItem?._id) {
                      handleDelete(selectedItem._id);
                      setSelectedItem(null);
                    }
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </motion.div>
      </Container>
    </Layout>
  );
};

PendingCompliancePage.defaultProps = {
  initialData: [],
  pageSize: 10,
};


const styles = {
  tableContainer: {
    marginTop: 3,
    marginBottom: 3,
  },
  headerCell: {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
  },
  actionButton: {
    marginLeft: 1,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
};

export default PendingCompliancePage;
